import React, {useState, useEffect, useRef} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";

const VLAAdminPartnerDeviceRequestsTable = () => {
    const [requestsData, setRequestsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const isInitialLoad = useRef(true);

    useEffect(() => {
        const fetchRequestsData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("prismax_admin_token");
                const response = await fetch(
                    `${PRISMAX_BACKEND_URL}/api/admin/get-partner-device-requests?page=${currentPage}&page_size=${itemsPerPage}`,
                    {
                        headers: {
                            "Authorization": `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch partner device requests');
                }
                const data = await response.json();
                if (data.success && data.data) {
                    setRequestsData(data.data);
                    setTotalPages(data.total_pages || 1);
                } else {
                    throw new Error('Invalid data format');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                isInitialLoad.current = false;
            }
        };

        fetchRequestsData();
    }, [currentPage]);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Go to previous page
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    if (loading && isInitialLoad.current) {
        return <div className={styles.loading}>Loading partner device requests...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <h2 className={styles.tableTitle}>Partner Device Listing Requests</h2>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>ID</th>
                        <th>User ID</th>
                        <th>Manufacturer</th>
                        <th>Model Name</th>
                        <th>Device Type</th>
                        <th>Serial Number Info</th>
                        <th>Product URL</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>Created At</th>
                    </tr>
                    </thead>
                    <tbody>
                    {requestsData.length > 0 ? (
                        requestsData.map((item) => (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>{item.user_id}</td>
                                <td>{item.manufacturer}</td>
                                <td>{item.model_name}</td>
                                <td>{item.device_type}</td>
                                <td><pre>{JSON.stringify(item.serial_number_info, null, 2)}</pre></td>
                                <td>{item.product_url}</td>
                                <td>{item.first_name}</td>
                                <td>{item.last_name}</td>
                                <td>{item.email}</td>
                                <td>{new Date(item.created_at).toLocaleString()}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="11" className={styles.noData}>
                                No partner device requests found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {requestsData.length > 0 && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            // Show limited page numbers with ellipsis for better UX
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>

                </div>
            )}
        </div>
    );
};

export default VLAAdminPartnerDeviceRequestsTable;
